(() => {
  if (customElements.get('marquee-component')) return;

class MarqueeComponent extends HTMLElement {
  constructor() {
    super();

    this.speed = 12;
    this.direction = 'ltr';
    this.pauseOnHover = true;
    this.paused = false;
    this.marqueeVisible = true;
    this.isInitialized = false;
    this.lifecycleActive = false;

    this.container = null;
    this.content = null;
    this.originalItems = [];
    this.clones = [];
    this.cloneSetCount = 0;

    this.containerWidth = 0;
    this.originalContentWidth = 0;
    this.itemWidth = 0;
    this.translateX = 0;

    this.animationId = null;
    this.initRaf = null;
    this.measureRaf = null;
    this.forceNextMeasure = false;
    this.lastFrameTime = null;
    this.connectionToken = 0;

    this.resizeObserver = null;
    this.intersectionObserver = null;
    this.motionQuery = null;

    this.animateFrame = this.animateFrame.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
    this.handleFontLoading = this.handleFontLoading.bind(this);
  }

  connectedCallback() {
    if (this.lifecycleActive) return;

    this.lifecycleActive = true;
    this.paused = false;
    this.marqueeVisible = true;
    const token = ++this.connectionToken;

    // The section stylesheet is render-blocking. One frame is enough to let
    // parser/editor insertion finish without relying on an arbitrary timeout.
    this.initRaf = requestAnimationFrame(() => {
      this.initRaf = null;
      if (!this.lifecycleActive || token !== this.connectionToken || !this.isConnected) return;
      this.initialize(token);
    });
  }

  disconnectedCallback() {
    this.lifecycleActive = false;
    this.connectionToken += 1;
    this.isInitialized = false;

    if (this.initRaf !== null) {
      cancelAnimationFrame(this.initRaf);
      this.initRaf = null;
    }

    if (this.measureRaf !== null) {
      cancelAnimationFrame(this.measureRaf);
      this.measureRaf = null;
    }

    this.forceNextMeasure = false;
    this.stopAnimation();
    this.removeEventListeners();
    this.removeClones();

    if (this.content) this.content.style.removeProperty('transform');
    if (this.container) this.container.style.removeProperty('justify-content');

    this.container = null;
    this.content = null;
    this.originalItems = [];
    this.containerWidth = 0;
    this.originalContentWidth = 0;
    this.itemWidth = 0;
    this.translateX = 0;
  }

  initialize(token) {
    this.container = this.querySelector('.marquee-container');
    this.content = this.querySelector('.marquee-content');

    if (!this.container || !this.content) return;

    this.readSettings();
    this.removeClones();
    this.refreshOriginalItems();

    if (!this.originalItems.length) return;

    this.setupEventListeners();
    this.isInitialized = true;
    this.measureAndBuild({ force: true, resetPosition: true });
    this.startAnimation();

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!this.lifecycleActive || token !== this.connectionToken || !this.isConnected) return;
        this.scheduleMeasure();
      });
    }
  }

  readSettings() {
    const speedSetting = this.dataset.speedSetting || 'normal';
    const speedMap = { slow: 3, normal: 12, fast: 24 };

    this.speed = speedMap[speedSetting] || speedMap.normal;
    this.direction = this.dataset.direction === 'rtl' ? 'rtl' : 'ltr';
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
  }

  refreshOriginalItems() {
    if (!this.content) return false;

    const nextItems = Array.from(this.content.children).filter((item) => !item.classList.contains('marquee-clone'));
    const changed =
      nextItems.length !== this.originalItems.length || nextItems.some((item, index) => item !== this.originalItems[index]);

    if (!changed) return false;

    if (this.resizeObserver) {
      this.originalItems.forEach((item) => this.resizeObserver.unobserve(item));
    }

    this.originalItems = nextItems;

    if (this.resizeObserver) {
      this.originalItems.forEach((item) => this.resizeObserver.observe(item));
    }

    return true;
  }

  measureOriginalContentWidth() {
    return this.originalItems.reduce((total, item) => {
      const rect = item.getBoundingClientRect();
      const styles = getComputedStyle(item);
      const marginLeft = Number.parseFloat(styles.marginLeft) || 0;
      const marginRight = Number.parseFloat(styles.marginRight) || 0;

      return total + rect.width + marginLeft + marginRight;
    }, 0);
  }

  getRequiredCloneSetCount(contentWidth, containerWidth) {
    if (contentWidth <= 0 || containerWidth <= 0) return 0;

    // Keep at least one complete repeated set beyond the viewport while the
    // transform moves through one full pitch in either direction.
    return Math.max(2, Math.ceil(containerWidth / contentWidth) + 1);
  }

  measureAndBuild({ force = false, resetPosition = false } = {}) {
    if (!this.isInitialized || !this.container || !this.content) return;

    const originalsChanged = this.refreshOriginalItems();
    if (!this.originalItems.length) {
      this.stopAnimation();
      this.removeClones();
      this.content.style.removeProperty('transform');
      this.itemWidth = 0;
      this.originalContentWidth = 0;
      return;
    }

    const nextContainerWidth = this.container.getBoundingClientRect().width;
    const nextContentWidth = this.measureOriginalContentWidth();

    if (nextContainerWidth <= 0 || nextContentWidth <= 0) return;

    const nextCloneSetCount = this.getRequiredCloneSetCount(nextContentWidth, nextContainerWidth);
    const dimensionsChanged =
      force ||
      originalsChanged ||
      Math.abs(nextContainerWidth - this.containerWidth) >= 0.25 ||
      Math.abs(nextContentWidth - this.itemWidth) >= 0.25 ||
      nextCloneSetCount !== this.cloneSetCount;

    this.containerWidth = nextContainerWidth;
    this.originalContentWidth = nextContentWidth;

    if (this.prefersReducedMotion()) {
      this.itemWidth = nextContentWidth;
      this.removeClones();
      this.translateX = 0;
      this.updatePosition();
      this.container.style.justifyContent = this.itemWidth <= this.containerWidth ? 'center' : 'flex-start';
      return;
    }

    this.container.style.removeProperty('justify-content');
    if (!dimensionsChanged && this.clones.length) return;

    const previousWidth = this.itemWidth;
    const previousTranslate = this.translateX;

    this.itemWidth = nextContentWidth;
    this.removeClones();
    this.createClones(nextCloneSetCount);

    if (resetPosition || previousWidth <= 0) {
      this.translateX = this.direction === 'ltr' ? 0 : -this.itemWidth;
    } else {
      this.translateX = this.normalizeTranslate(previousTranslate, this.itemWidth);
    }

    this.updatePosition();
  }

  normalizeTranslate(value, width) {
    if (!Number.isFinite(value) || width <= 0) return this.direction === 'ltr' ? 0 : -width;

    const remainder = ((value % width) + width) % width;
    if (remainder < 0.01 || width - remainder < 0.01) {
      return this.direction === 'ltr' ? 0 : -width;
    }

    return remainder - width;
  }

  createClones(setCount) {
    if (!this.content || !this.originalItems.length || setCount <= 0) return;

    const fragment = document.createDocumentFragment();

    for (let setIndex = 0; setIndex < setCount; setIndex += 1) {
      this.originalItems.forEach((item) => {
        const clone = item.cloneNode(true);
        this.prepareClone(clone);
        fragment.appendChild(clone);
        this.clones.push(clone);
      });
    }

    this.content.appendChild(fragment);
    this.cloneSetCount = setCount;
  }

  prepareClone(clone) {
    clone.classList.add('marquee-clone');
    clone.removeAttribute('data-marquee-original');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.inert = true;

    [clone, ...clone.querySelectorAll('*')].forEach((node) => {
      node.removeAttribute('id');
      node.removeAttribute('data-shopify-editor-block');
    });

    clone
      .querySelectorAll('a, button, input, select, textarea, summary, [contenteditable], [tabindex]')
      .forEach((element) => element.setAttribute('tabindex', '-1'));
  }

  removeClones() {
    this.clones.forEach((clone) => clone.remove());
    this.clones = [];
    this.cloneSetCount = 0;

    // Also clean stale clones left by an interrupted older lifecycle.
    this.content?.querySelectorAll(':scope > .marquee-clone').forEach((clone) => clone.remove());
  }

  scheduleMeasure(force = false) {
    if (!this.lifecycleActive || !this.isInitialized) return;

    this.forceNextMeasure ||= force;
    if (this.measureRaf !== null) return;

    this.measureRaf = requestAnimationFrame(() => {
      this.measureRaf = null;
      const shouldForce = this.forceNextMeasure;
      this.forceNextMeasure = false;

      if (!this.lifecycleActive || !this.isConnected) return;
      this.measureAndBuild({ force: shouldForce });
    });
  }

  updatePosition() {
    if (!this.content) return;
    this.content.style.transform = `translate3d(${this.translateX}px, 0, 0)`;
  }

  animateFrame(timestamp) {
    this.animationId = null;

    if (!this.canAnimate()) {
      this.lastFrameTime = null;
      return;
    }

    if (this.lastFrameTime === null) {
      this.lastFrameTime = timestamp;
    } else {
      const elapsed = Math.min(Math.max(timestamp - this.lastFrameTime, 0), 64);
      const distance = (this.speed * elapsed) / 1000;
      this.lastFrameTime = timestamp;

      if (this.direction === 'ltr') {
        this.translateX -= distance;
        if (this.translateX <= -this.itemWidth) this.translateX = this.normalizeTranslate(this.translateX, this.itemWidth);
      } else {
        this.translateX += distance;
        if (this.translateX >= 0) this.translateX = this.normalizeTranslate(this.translateX, this.itemWidth);
      }

      this.updatePosition();
    }

    this.animationId = requestAnimationFrame(this.animateFrame);
  }

  canAnimate() {
    return (
      this.isInitialized &&
      this.itemWidth > 0 &&
      !this.paused &&
      this.marqueeVisible &&
      !document.hidden &&
      !this.prefersReducedMotion()
    );
  }

  startAnimation() {
    if (this.animationId !== null || !this.canAnimate()) return;

    this.lastFrameTime = null;
    this.animationId = requestAnimationFrame(this.animateFrame);
  }

  stopAnimation() {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    this.lastFrameTime = null;
  }

  pause() {
    this.paused = true;
    this.stopAnimation();
  }

  resume() {
    this.paused = false;
    this.startAnimation();
  }

  prefersReducedMotion() {
    return Boolean(this.motionQuery?.matches);
  }

  handleMouseEnter() {
    this.pause();
  }

  handleMouseLeave() {
    this.resume();
  }

  handleResize() {
    this.scheduleMeasure();
  }

  handleIntersection(entries) {
    const entry = entries[0];
    if (!entry) return;

    this.marqueeVisible = entry.isIntersecting;
    if (this.marqueeVisible) {
      this.startAnimation();
    } else {
      this.stopAnimation();
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stopAnimation();
    } else {
      this.startAnimation();
    }
  }

  handleMotionPreferenceChange() {
    if (this.prefersReducedMotion()) {
      this.stopAnimation();
      this.removeClones();
      this.translateX = 0;
      this.updatePosition();
      if (this.container) {
        this.container.style.justifyContent = this.itemWidth <= this.containerWidth ? 'center' : 'flex-start';
      }
      return;
    }

    if (this.container) this.container.style.removeProperty('justify-content');
    this.measureAndBuild({ force: true, resetPosition: true });
    this.startAnimation();
  }

  handleFontLoading() {
    this.scheduleMeasure();
  }

  setupEventListeners() {
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
    }

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this.handleMotionPreferenceChange);
    } else {
      this.motionQuery.addListener(this.handleMotionPreferenceChange);
    }

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.container);
      this.originalItems.forEach((item) => this.resizeObserver.observe(item));
    } else {
      window.addEventListener('resize', this.handleResize, { passive: true });
    }

    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, { rootMargin: '80px' });
      this.intersectionObserver.observe(this);
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (document.fonts?.addEventListener) {
      document.fonts.addEventListener('loadingdone', this.handleFontLoading);
      document.fonts.addEventListener('loadingerror', this.handleFontLoading);
    }
  }

  removeEventListeners() {
    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);

    if (this.motionQuery) {
      if (this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.handleMotionPreferenceChange);
      } else {
        this.motionQuery.removeListener(this.handleMotionPreferenceChange);
      }
    }
    this.motionQuery = null;

    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = null;
    window.removeEventListener('resize', this.handleResize);

    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    this.intersectionObserver = null;

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    if (document.fonts?.removeEventListener) {
      document.fonts.removeEventListener('loadingdone', this.handleFontLoading);
      document.fonts.removeEventListener('loadingerror', this.handleFontLoading);
    }
  }
}

customElements.define('marquee-component', MarqueeComponent);
})();
